import { LegalLayout, LegalSection } from "@/components/LegalLayout";

const SUBPROCESSORS = [
  { name: "OpenAI", purpose: "AI-generering og analyser" },
  { name: "Emergent", purpose: "Drift, hosting og udvikling af Aicom AI (AI-infrastruktur)" },
  { name: "MongoDB", purpose: "Database og brugeradministration" },
  { name: "Stripe", purpose: "Betaling og abonnementer" },
  { name: "Resend", purpose: "Udsendelse af e-mails og notifikationer" },
  { name: "Mailchimp", purpose: "Nyhedsbreve og e-mailmarkedsføring" },
  { name: "Google Analytics 4", purpose: "Statistik og analyse af brug af hjemmesiden, inkl. server-til-server-hændelser (Measurement Protocol) (kun ved samtykke)" },
  { name: "Microsoft Clarity", purpose: "Adfærdsanalyse, session-optagelser og heatmaps (kun ved samtykke)" },
];

export default function PrivacyPage() {
  return (
    <LegalLayout
      eyebrow="Cookie- & privatlivspolitik"
      title="Cookie- og privatlivspolitik"
      updated="juni 2026"
      intro="Hos PointTaken respekterer vi dit privatliv og behandler dine personoplysninger med omhu. Denne privatlivspolitik beskriver, hvilke oplysninger vi indsamler, hvorfor vi gør det, hvordan vi behandler dem, og hvilke rettigheder du har."
    >

        <LegalSection title="1. Dataansvarlig">
          <p>Den dataansvarlige for behandlingen af dine personoplysninger er:</p>
          <p className="text-foreground">
            PointTaken I/S<br />
            CVR-nr.: 38540807<br />
            E-mail: <a href="mailto:kontakt@pointtaken.dk" className="underline underline-offset-4 hover:no-underline">kontakt@pointtaken.dk</a><br />
            Telefon: 53 82 38 30
          </p>
        </LegalSection>

        <LegalSection title="2. Hvilke oplysninger indsamler vi?">
          <p>Afhængigt af hvordan du anvender vores hjemmeside og tjenester, kan vi behandle følgende oplysninger:</p>
          <h3 className="font-display text-lg font-medium text-foreground pt-2">Kontaktoplysninger</h3>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Navn</li>
            <li>E-mailadresse</li>
            <li>Telefonnummer</li>
            <li>Virksomhed</li>
            <li>Jobtitel</li>
          </ul>
          <h3 className="font-display text-lg font-medium text-foreground pt-2">Kontooplysninger</h3>
          <p>Hvis du opretter en konto i Aicom AI:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Loginoplysninger</li>
            <li>Abonnementsoplysninger</li>
            <li>Brugerindstillinger</li>
          </ul>
          <h3 className="font-display text-lg font-medium text-foreground pt-2">Virksomhedsoplysninger</h3>
          <p>Hvis du anvender Aicom AI, kan du vælge at dele oplysninger om din virksomhed, eksempelvis website, branche, produkter, ydelser, målgrupper, vision, mission, værdier, tone of voice, konkurrenter, LinkedIn-profiler, kommunikationsmateriale, brandfortællinger, cases, nyhedsbreve, pressemeddelelser, dokumenter, PDF'er, præsentationer, logoer og billeder.</p>
          <p>Du bestemmer selv, hvilke oplysninger du ønsker at dele.</p>
        </LegalSection>

        <LegalSection title="3. Brug af Aicom AI">
          <p>Når du anvender Aicom AI, kan du indtaste tekst, dokumenter og andre oplysninger for at få analyser, kommunikationsforslag eller AI-genereret indhold.</p>
          <p>Oplysningerne behandles alene med det formål at levere de funktioner, du vælger at benytte.</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Vi sælger ikke dine data.</li>
            <li>Vi deler ikke dine data med andre kunder.</li>
          </ul>
          <p>Det indhold, du indtaster eller uploader, behandles af vores AI-leverandør OpenAI for at kunne levere analyser og AI-genereret indhold. OpenAI anvender ikke dine data til at træne deres modeller, men kan opbevare dem i op til 30 dage af hensyn til driftssikkerhed og misbrugskontrol. Vi anbefaler, at du ikke uploader følsomme personoplysninger.</p>
        </LegalSection>

        <LegalSection title="4. Download af guides og andet indhold">
          <p>På vores hjemmeside tilbyder vi blandt andet guides, rapporter, e-bøger og andet fagligt materiale. Når du downloader materiale, kan vi bede om navn, e-mail, virksomhed og jobtitel.</p>
          <p>Oplysningerne anvendes til:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>at sende det ønskede materiale</li>
            <li>at følge op på din interesse</li>
            <li>at udsende relevante nyheder, invitationer og fagligt indhold, hvis du har givet samtykke</li>
            <li>at forbedre vores markedsføring og kommunikation</li>
          </ul>
          <p>Du kan til enhver tid afmelde dig vores nyhedsbrev.</p>
        </LegalSection>

        <LegalSection title="5. Formål med behandlingen">
          <p>Vi behandler personoplysninger for at:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>levere vores ydelser</li>
            <li>levere Aicom AI</li>
            <li>administrere abonnementer</li>
            <li>yde support</li>
            <li>besvare henvendelser</li>
            <li>forbedre vores produkter</li>
            <li>sende driftsinformation</li>
            <li>udsende nyhedsbreve (med samtykke)</li>
            <li>overholde lovgivningen</li>
          </ul>
        </LegalSection>

        <LegalSection title="6. Retsgrundlag">
          <p>Vi behandler personoplysninger på baggrund af opfyldelse af en aftale, samtykke, legitim interesse og retlig forpligtelse afhængigt af den konkrete behandling.</p>
        </LegalSection>

        <LegalSection title="7. Cookies">
          <p>Vi anvender cookies og lignende teknologier til at få hjemmesiden til at fungere samt til statistik, analyse og markedsføring. Nødvendige cookies sættes altid, mens statistik- og marketingcookies kun sættes, hvis du giver samtykke i vores cookiebanner.</p>
          <div className="rounded-2xl border border-border overflow-hidden mt-2">
            <div className="grid grid-cols-[minmax(110px,1fr)_1.4fr_1.2fr] bg-secondary/60 text-foreground text-sm font-medium">
              <div className="px-4 py-3 border-b border-border">Kategori</div>
              <div className="px-4 py-3 border-b border-border">Eksempler</div>
              <div className="px-4 py-3 border-b border-border">Formål</div>
            </div>
            {[
              ["Nødvendige", "Login/session (JWT-token), samtykkevalg, sikkerhed", "Får siden og din konto til at fungere. Sættes altid og kræver ikke samtykke."],
              ["Statistik", "Google Analytics 4 (_ga, _ga_*), Microsoft Clarity (_clck, _clsk)", "Måler brug, adfærd og heatmaps, så vi kan forbedre siden. Kun ved samtykke."],
              ["Marketing", "Annoncerings- og remarketing-cookies", "Bruges til at vise relevant annoncering. Kun ved samtykke."],
            ].map(([cat, ex, form], i, arr) => (
              <div key={cat} className={`grid grid-cols-[minmax(110px,1fr)_1.4fr_1.2fr] text-sm ${i < arr.length - 1 ? "border-b border-border" : ""}`}>
                <div className="px-4 py-3 text-foreground font-medium">{cat}</div>
                <div className="px-4 py-3 text-muted-foreground">{ex}</div>
                <div className="px-4 py-3 text-muted-foreground">{form}</div>
              </div>
            ))}
          </div>
          <p>Til statistik anvender vi <span className="text-foreground">Google Analytics 4</span> og <span className="text-foreground">Microsoft Clarity</span>. Vores statistik suppleres i visse tilfælde med server-til-server-hændelser (Google Analytics Measurement Protocol), fx ved gennemført køb, så målingen bliver retvisende. Både browser- og server-side-statistik aktiveres kun, hvis du har givet samtykke til statistik. Microsoft Clarity kan optage anonymiserede sessioner og heatmaps og sættes ligeledes kun ved samtykke.</p>
          <p>Du kan til enhver tid ændre eller trække dit samtykke tilbage:</p>
          <p>
            <button type="button" onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
              className="inline-flex items-center gap-2 rounded-full bg-foreground text-background px-5 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity"
              data-testid="privacy-open-cookie-settings">
              Åbn cookieindstillinger
            </button>
          </p>
        </LegalSection>

        <LegalSection title="8. Databehandlere">
          <p>For at kunne levere vores tjenester samarbejder vi med en række databehandlere og teknologileverandører. Disse leverandører behandler alene oplysninger efter vores instruks og i overensstemmelse med gældende databeskyttelseslovgivning.</p>
          <p>Vi anvender blandt andet:</p>
          <div className="rounded-2xl border border-border overflow-hidden mt-2">
            <div className="grid grid-cols-[minmax(120px,1fr)_2fr] bg-secondary/60 text-foreground text-sm font-medium">
              <div className="px-4 py-3 border-b border-border">Leverandør</div>
              <div className="px-4 py-3 border-b border-border">Formål</div>
            </div>
            {SUBPROCESSORS.map((s, i) => (
              <div key={s.name} className={`grid grid-cols-[minmax(120px,1fr)_2fr] text-sm ${i < SUBPROCESSORS.length - 1 ? "border-b border-border" : ""}`}>
                <div className="px-4 py-3 text-foreground font-medium">{s.name}</div>
                <div className="px-4 py-3 text-muted-foreground">{s.purpose}</div>
              </div>
            ))}
          </div>
          <p>Visse leverandører er etableret uden for EU/EØS (USA), herunder vores hosting-/infrastrukturleverandør (MongoDB Atlas, USA) samt OpenAI, Stripe, Google (Analytics) og Microsoft (Clarity). Vi arbejder aktivt på at sikre et lovligt overførselsgrundlag for disse overførsler — herunder EU-Kommissionens standardkontraktbestemmelser (SCC) samt EU-U.S. Data Privacy Framework for Google og Microsoft, og mulig flytning af hosting til EU/EØS. Kontakt os på kontakt@pointtaken.dk for den aktuelle status.</p>
        </LegalSection>

        <LegalSection title="9. Opbevaring">
          <p>Vi opbevarer kun personoplysninger, så længe det er nødvendigt. Hvis du sletter din konto, vil dine oplysninger blive slettet eller anonymiseret, medmindre vi er forpligtede til at opbevare dem efter lovgivningen. Regnskabsmateriale opbevares eksempelvis efter bogføringslovens regler.</p>
        </LegalSection>

        <LegalSection title="10. Datasikkerhed">
          <p>Vi arbejder løbende på at beskytte dine oplysninger. Vi anvender tekniske og organisatoriske sikkerhedsforanstaltninger, som skal forhindre uautoriseret adgang, tab eller misbrug. Kun medarbejdere med et arbejdsbetinget behov har adgang til personoplysninger.</p>
        </LegalSection>

        <LegalSection title="11. Dine rettigheder">
          <p>Du har ret til:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>indsigt i de oplysninger, vi behandler om dig</li>
            <li>at få urigtige oplysninger rettet</li>
            <li>at få oplysninger slettet, når lovgivningen giver mulighed for det</li>
            <li>at få behandlingen begrænset</li>
            <li>dataportabilitet</li>
            <li>at gøre indsigelse mod behandlingen</li>
            <li>at trække et samtykke tilbage</li>
          </ul>
          <p>Ønsker du at gøre brug af dine rettigheder, kan du kontakte os på <a href="mailto:kontakt@pointtaken.dk" className="text-foreground underline underline-offset-4 hover:no-underline">kontakt@pointtaken.dk</a>. Du kan til enhver tid hente dine uploadede dokumenter direkte i Vidensbanken. Ønsker du at få din øvrige kontodata udleveret eller din konto slettet, kan du skrive til os. Vi besvarer anmodninger uden unødig forsinkelse og senest inden for én måned.</p>
        </LegalSection>

        <LegalSection title="12. Klage">
          <p>Hvis du mener, at vi behandler dine personoplysninger i strid med gældende regler, har du ret til at klage til:</p>
          <p className="text-foreground">
            Datatilsynet<br />
            Carl Jacobsens Vej 35<br />
            2500 Valby
          </p>
          <p>Du kan læse mere på <a href="https://www.datatilsynet.dk" target="_blank" rel="noopener noreferrer" className="text-foreground underline underline-offset-4 hover:no-underline">Datatilsynets hjemmeside</a>.</p>
        </LegalSection>

        <LegalSection title="13. Ændringer">
          <p>Vi kan løbende opdatere denne privatlivspolitik. Den seneste version vil altid være tilgængelig på vores hjemmeside.</p>
        </LegalSection>

        <LegalSection title="14. Kontakt">
          <p>Har du spørgsmål til vores behandling af personoplysninger, er du altid velkommen til at kontakte os.</p>
          <p className="text-foreground">
            PointTaken I/S<br />
            E-mail: <a href="mailto:kontakt@pointtaken.dk" className="underline underline-offset-4 hover:no-underline">kontakt@pointtaken.dk</a><br />
            Telefon: 53 82 38 30
          </p>
        </LegalSection>

        <LegalSection title="Har du brug for en databehandleraftale med PointTaken, som ejer Aicom?">
          <p>Som virksomhedsejer er du ansvarlig for de persondata, som du indsamler. Har du indsamlet persondata, som videresendes til PointTaken i markedsføringsøjemed, skal du som den dataansvarlige bruge en databehandleraftale med os. PointTaken kan ikke holdes ansvarlig for eventuelle fejl og mangler i vores kunders politik om persondata. Det er til enhver tid virksomhedsejeren selv, som er ansvarlig for, at virksomheden lever op til gældende lovgivning.</p>
          <p>Herunder kan du downloade en skabelon til en databehandleraftale, som Datatilsynet har lavet.</p>
          <p>
            <a href="https://www.datatilsynet.dk/presse-og-nyheder/nyhedsarkiv/2018/feb/ny-skabelon-skal-hjaelpe-virksomheder-og-myndigheder-med-at-blive-klar-til-databeskyttelsesforordningen"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-foreground text-background px-5 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity"
              data-testid="download-dpa-template">
              Download skabelon til databehandleraftale
            </a>
          </p>
        </LegalSection>
    </LegalLayout>
  );
}
